/**
 * The browser-side adapter: the firebase JS SDK, used by the Studio and the admin app.
 *
 * Every write here also has to pass the security rules, which is the point: the client may
 * request a render and answer the approval gate, and the rules refuse the rest.
 */
import { AlreadyExistsError, NotFoundError } from '@atlas-nook/story';
import { deleteField, doc, getDoc, runTransaction, updateDoc } from 'firebase/firestore';
function isNotFound(error) {
    if (typeof error !== 'object' || error === null)
        return false;
    const code = error.code;
    return code === 'not-found' || code === 5;
}
/** Same rule as the admin adapter: `undefined` means remove the field, not "leave it". */
function toUpdate(patch) {
    const update = {};
    for (const key of Object.keys(patch)) {
        const value = patch[key];
        update[key] = value === undefined ? deleteField() : value;
    }
    return update;
}
/**
 * A `ResolveStore` over one client-SDK collection.
 *
 * The JS SDK has no create-only write, so `create` runs a transaction: read the address,
 * refuse if it is taken, write if it is not. The transaction is what makes losing the race
 * an `AlreadyExistsError` instead of a silent overwrite of someone else's in-flight work.
 */
export function createClientStore(db, collectionPath) {
    const ref = (id) => doc(db, collectionPath, id);
    return {
        async get(id) {
            const snapshot = await getDoc(ref(id));
            if (!snapshot.exists())
                return null;
            return { ...snapshot.data(), id: snapshot.id };
        },
        async create(record) {
            await runTransaction(db, async (transaction) => {
                const existing = await transaction.get(ref(record.id));
                if (existing.exists())
                    throw new AlreadyExistsError(record.id);
                transaction.set(ref(record.id), record);
            });
        },
        async update(id, patch) {
            const update = toUpdate(patch);
            if (Object.keys(update).length === 0)
                return;
            try {
                await updateDoc(ref(id), update);
            }
            catch (error) {
                if (isNotFound(error))
                    throw new NotFoundError(id);
                throw error;
            }
        },
    };
}
/**
 * The catalog illustration helpers, re-exported so `@atlas-nook/firestore-store/client` is
 * the one browser-side import — the subpath is what apps reach for, and splitting these
 * across two entry points would only invite the index (and `firebase-admin` with it).
 */
export { checkIllustration, checkPhoto, illustrationPathFor, illustrationUrl, mediumAssetUrl, photoPathFor, ILLUSTRATION_MAX_BYTES, ILLUSTRATION_TYPES, PHOTO_CLIP_MAX_BYTES, PHOTO_CLIP_TYPES, PHOTO_STILL_MAX_BYTES, PHOTO_STILL_TYPES, } from './clientAssets.js';
//# sourceMappingURL=clientStore.js.map